import React from "react";
import { ColDef } from "ag-grid-community";
import Asset from "../../../models/asset";
import Document from "../../../models/document";
import DocumentCategory from "../../../models/document-category";
import DocumentSubCategory from "../../../models/document-subcategory";
import Subcompany from "../../../models/subcompany";
import Tag from "../../../models/tag";
import { buildAdditionalFieldColumns } from "./additionalFieldColumns";
import { buildDocumentMetadataColumns } from "./documentMetadataColumns";
import { buildFileColumns } from "./fileColumns";
import { buildRelationshipColumns } from "./relationshipColumns";

interface UseDocumentSpreadsheetColumnsParams {
  additionalFields: any;
  changedCells: { [key: number]: Set<string> };
  setChangedCells: React.Dispatch<
    React.SetStateAction<{ [key: number]: Set<string> }>
  >;
  subcompany: Subcompany[];
  downloadingIds: Set<number>;
  previewingIds: Set<number>;
  documentCategoryId: DocumentCategory[];
  documentSubcategoryId: DocumentSubCategory[];
  assets: Asset[];
  tags: Tag[];
  deletingIds: number[];
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  setGridDateValue: (params: any, field: string, value: string | null) => void;
  markCellChanged: (rowId: number, field?: string) => void;
  formatDateForInput: (dateString?: string | null) => string;
  openNativeDatePicker: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  stopGridMouseEvent: (event: React.SyntheticEvent) => void;
  isExpired: (expirationDate?: string) => boolean;
  isPdfFile: (filePath?: string | null) => boolean;
  handleDownloadClick: (downloadDoc: Document) => void;
  handlePreviewPdfClick: (previewDoc: Document) => void;
  handleOpenVersionsModal: (document: Document) => void;
  handleFileUpload: (
    file: File,
    documentId: number | undefined,
    documentData: Document,
  ) => Promise<boolean>;
  confirmDelete: (id: number) => void;
}

const useDocumentSpreadsheetColumns = ({
  additionalFields,
  changedCells,
  setChangedCells,
  subcompany,
  downloadingIds,
  previewingIds,
  documentCategoryId,
  documentSubcategoryId,
  assets,
  tags,
  deletingIds,
  setDocuments,
  setGridDateValue,
  markCellChanged,
  formatDateForInput,
  openNativeDatePicker,
  stopGridMouseEvent,
  isExpired,
  isPdfFile,
  handleDownloadClick,
  handlePreviewPdfClick,
  handleOpenVersionsModal,
  handleFileUpload,
  confirmDelete,
}: UseDocumentSpreadsheetColumnsParams): ColDef[] => {
  const relationshipColumns = buildRelationshipColumns({
    assets,
    changedCells,
    documentCategoryId,
    documentSubcategoryId,
    markCellChanged,
    subcompany,
  });

  const metadataColumns = buildDocumentMetadataColumns({
    changedCells,
    deletingIds,
    formatDateForInput,
    handleOpenVersionsModal,
    isExpired,
    markCellChanged,
    setChangedCells,
    setDocuments,
    setGridDateValue,
    stopGridMouseEvent,
    tags,
    confirmDelete,
  });

  const fileColumns = buildFileColumns({
    changedCells,
    downloadingIds,
    previewingIds,
    setChangedCells,
    markCellChanged,
    isPdfFile,
    handleDownloadClick,
    handlePreviewPdfClick,
    handleFileUpload,
  });

  const additionalFieldColumns = buildAdditionalFieldColumns({
    additionalFields,
    changedCells,
    setChangedCells,
    setGridDateValue,
    markCellChanged,
    formatDateForInput,
    openNativeDatePicker,
    stopGridMouseEvent,
  });

  return [
    metadataColumns[0],
    relationshipColumns[3],
    metadataColumns[4],
    fileColumns[0],
    relationshipColumns[0],
    fileColumns[1],
    metadataColumns[10],
    relationshipColumns[1],
    relationshipColumns[2],
    metadataColumns[1],
    ...metadataColumns.slice(2, 4),
    ...metadataColumns.slice(5, 10),
    ...additionalFieldColumns,
    metadataColumns[11],
  ];
};

export default useDocumentSpreadsheetColumns;
